import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, Switch } from '@mui/material';
import { MouseEvent, memo, useCallback, useState } from 'react';

export type ActionMenuItem = {
  key: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  toggle?: { checked: boolean; helperText?: string };
};

type Props = {
  items: ActionMenuItem[];
};

export const ActionMenu = memo(({ items }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const close = useCallback(() => setAnchorEl(null), []);

  const stopPropagation = useCallback(
    (event: MouseEvent<HTMLDivElement>) => event.stopPropagation(),
    [],
  );

  return (
    <>
      <button
        type="button"
        aria-label="Post actions"
        onClick={open}
        className="flex size-8 items-center justify-center rounded-full text-[#2C3548] hover:bg-[#6367ef0a]"
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        onClick={stopPropagation}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
            },
          },
        }}
      >
        <div className="flex min-w-[179px] flex-col py-1">
          {items.map((item) =>
            item.toggle ? (
              <div
                key={item.key}
                data-testid={`actionMenu-${item.key}`}
                className="flex flex-col"
              >
                <div className="flex h-10 items-center justify-between gap-2 px-3">
                  <span className="text-base font-medium text-black">
                    {item.label}
                  </span>
                  <Switch
                    checked={item.toggle.checked}
                    disabled={item.disabled}
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      event.stopPropagation();
                      item.onClick?.();
                      close();
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5F1ECC',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: '#5F1ECC',
                        },
                    }}
                  />
                </div>
                {item.toggle.helperText && (
                  <span className="max-w-[180px] px-3 pb-2 text-[11px] text-gray-500">
                    {item.toggle.helperText}
                  </span>
                )}
              </div>
            ) : (
              <button
                key={item.key}
                type="button"
                disabled={item.disabled}
                data-testid={`actionMenu-${item.key}`}
                onClick={(event) => {
                  event.stopPropagation();
                  item.onClick?.();
                  close();
                }}
                className={`px-3 py-2 text-left text-base font-medium hover:bg-gray-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent ${
                  item.destructive ? 'text-[#DA0000]' : 'text-black'
                }`}
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      </Popover>
    </>
  );
});

ActionMenu.displayName = 'ActionMenu';
